import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, GraduationCap, Home, BookOpen } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

const LearnToTeach = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleProgrammeCta = (programmeId) => {
    if (programmeId === 'phonics-training') {
      navigate('/teacher-training');
    } else {
      scrollToContact();
    }
  };

  const programmes = [
    {
      id: 'phonics-training',
      icon: GraduationCap,
      title: 'Phonics Teacher Training',
      subtitle: 'For teachers, assistant teachers, homeschool educators, and aspiring educators',
      description: 'Learn how to teach phonics and early reading step by step using a clear, structured, and child-friendly method.',
      features: [
        'Pure sounds',
        'Sound awareness',
        'Blending and segmenting',
        'CVC words and word-building',
        'Early reading steps',
        'Common reading mistakes',
        'Simple phonics lesson planning'
      ],
      bestFor: 'Teachers, assistant teachers, homeschool educators, and aspiring educators who want a structured method to teach phonics.',
      buttonText: 'View Teacher Training Programme'
    },
    {
      id: 'parent-educator',
      icon: Home,
      title: 'Parent Educator Programme',
      subtitle: 'For parents who want to support their child\'s overall learning at home',
      description: 'Understand how young children learn and how to create a calm, meaningful, and supportive home learning environment without pressure.',
      features: [
        'Reading readiness',
        'Learning routines',
        'Confidence-building',
        'Vocabulary development',
        'Communication skills',
        'Building independence',
        'Home learning habits',
        'Supporting your child with patience'
      ],
      bestFor: 'Parents who want to create a calm, supportive home learning environment for their child\'s overall development.',
      buttonText: 'Join Parent Programme'
    },
    {
      id: 'parent-phonics-guide',
      icon: BookOpen,
      title: 'Parent Phonics Guide',
      subtitle: 'For parents who want practical phonics support at home',
      description: 'Learn how to help your child with letter sounds, blending, simple words, and early reading practice at home.',
      features: [
        'Correct letter sounds',
        'Blending practice',
        'CVC words',
        'Simple reading games',
        'Gentle correction techniques',
        'Short home practice routines',
        'Building reading confidence'
      ],
      bestFor: 'Parents who want practical, hands-on phonics knowledge to support their child\'s early reading at home.',
      buttonText: 'Join Parent Phonics Guide'
    }
  ];

  const comparisonData = [
    {
      programme: 'Phonics Teacher Training',
      purpose: 'Teach phonics and early reading step by step',
      bestFor: 'Teachers, assistant teachers, homeschool educators, aspiring educators'
    },
    {
      programme: 'Parent Educator Programme',
      purpose: 'Support your child\'s overall learning at home',
      bestFor: 'Parents focused on holistic home learning'
    },
    {
      programme: 'Parent Phonics Guide',
      purpose: 'Practical phonics support for early reading at home',
      bestFor: 'Parents who want hands-on phonics tools'
    }
  ];

  return (
    <section id="learn-to-teach" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-blue-50 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            For Teachers & Parents
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Learn to Teach with Confidence
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-4">
            At Read Right Academy, we guide teachers and parents to support young children 
            with early reading, phonics, writing readiness, and learning confidence.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            Whether you are an educator, an aspiring teacher, or a parent who wants to help 
            your child at home, our programmes give you <strong>practical knowledge, clear steps, 
            and child-friendly strategies</strong> you can use with confidence.
          </p>
        </div>

        {/* 3 Programme Cards */}
        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {programmes.map((prog) => {
            const IconComponent = prog.icon;
            return (
              <Card
                key={prog.id}
                id={prog.id}
                data-testid={`teach-card-${prog.id}`}
                className="p-8 border-2 border-gray-100 hover:border-[#3B4BA0] hover:shadow-2xl transition-all duration-300 flex flex-col bg-white scroll-mt-24"
              >
                {/* Icon */}
                <div className="inline-flex w-16 h-16 bg-gradient-to-br from-[#3B4BA0] to-teal-500 rounded-xl items-center justify-center mb-6">
                  <IconComponent className="h-8 w-8 text-white" />
                </div>

                {/* Header */}
                <div className="mb-6 pb-6 border-b border-gray-100">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {prog.title}
                  </h3>
                  <p className="text-sm text-[#3B4BA0] font-semibold mb-4 leading-snug">
                    {prog.subtitle}
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {prog.description}
                  </p>
                </div>

                {/* Features */}
                <div className="mb-6 flex-grow">
                  <p className="text-sm font-semibold text-gray-900 mb-4">What's included:</p>
                  <ul className="space-y-3">
                    {prog.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Best For */}
                <div className="mb-6 bg-blue-50 rounded-lg p-4 border-l-4 border-[#3B4BA0]">
                  <p className="text-xs font-semibold text-[#3B4BA0] mb-1 uppercase tracking-wide">Best For</p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {prog.bestFor}
                  </p>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={() => handleProgrammeCta(prog.id)}
                  data-testid={`teach-cta-${prog.id}`}
                  className="w-full py-6 text-base font-semibold bg-[#3B4BA0] hover:bg-[#2d3a7a] text-white shadow-md hover:shadow-lg transition-all duration-300"
                >
                  {prog.buttonText}
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Comparison Table */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-10">
            Quick Comparison
          </h3>
          <div className="overflow-x-auto rounded-2xl shadow-lg border-2 border-gray-100">
            <table className="w-full bg-white">
              <thead>
                <tr className="bg-gradient-to-r from-[#3B4BA0] to-teal-600 text-white">
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wide">Programme</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wide">Main Purpose</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold uppercase tracking-wide">Best For</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="px-6 py-5 font-semibold text-gray-900">{row.programme}</td>
                    <td className="px-6 py-5 text-gray-700">{row.purpose}</td>
                    <td className="px-6 py-5 text-gray-700">{row.bestFor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Decision Helper */}
        <div className="bg-gradient-to-r from-[#3B4BA0] to-teal-600 rounded-2xl p-10 text-white shadow-xl">
          <h3 className="text-3xl font-bold text-center mb-8">
            Not Sure Which One Is Right for You?
          </h3>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h4 className="font-bold mb-2 text-lg">Phonics Teacher Training</h4>
              <p className="text-blue-50 text-sm leading-relaxed">
                Choose this if you want to <strong>teach phonics</strong> and early reading step by step.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h4 className="font-bold mb-2 text-lg">Parent Educator Programme</h4>
              <p className="text-blue-50 text-sm leading-relaxed">
                Choose this if you want to <strong>support your child's overall learning</strong> at home.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h4 className="font-bold mb-2 text-lg">Parent Phonics Guide</h4>
              <p className="text-blue-50 text-sm leading-relaxed">
                Choose this if you want <strong>practical phonics support</strong> for your child at home.
              </p>
            </div>
          </div>
          <div className="text-center">
            <Button
              size="lg"
              onClick={scrollToContact}
              data-testid="learn-to-teach-cta"
              className="bg-white text-[#3B4BA0] hover:bg-gray-100 text-lg px-10 py-6 shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              Get Personalised Recommendation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LearnToTeach;
